import {AiChatProTipType} from '../../components/ai-prompt-box-pro-tip/ai-chat-pro-tip-type';
import type {ProTipConfig} from '../../components/ai-prompt-box-pro-tip/ai-chat-pro-tip-config';
import {PRO_TIP_CONFIGS, LOW_RES_THRESHOLD} from '../../components/ai-prompt-box-pro-tip/ai-chat-pro-tip-config';
import type {ProTipContext} from '../../components/ai-prompt-box-pro-tip/ai-prompt-box-pro-tip.types';

const TEXTURE_REGEX = /\btextures?\b/i;
const VAGUE_PROMPT_MAX_LENGTH = 30;
const TOO_LONG_PROMPT_MIN_LENGTH = 450;
const LONG_CHAT_MIN_MESSAGES = 10;

function doesPromptMatchKeywords(prompt: string, keywords: string[]): boolean {
  const lowerPrompt = prompt.toLowerCase();
  return keywords.some((keyword) => {
    const escaped = keyword.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`\\b${escaped}\\b`, 'i').test(lowerPrompt);
  });
}

function hasKeywordMatch(prompt: string, config: ProTipConfig): boolean {
  return Boolean(prompt && config.triggerKeywords && doesPromptMatchKeywords(prompt, config.triggerKeywords));
}

interface TriggerContext {
  prompt: string;
  attachmentCount: number;
  prevAttachmentCount: number;
  context?: ProTipContext;
}

type TriggerEvaluator = (config: ProTipConfig, triggerContext: TriggerContext) => boolean;

const TRIGGER_EVALUATORS: Record<AiChatProTipType, TriggerEvaluator> = {
  [AiChatProTipType.ATTACHMENT_ADDED]: (_config, {attachmentCount, prevAttachmentCount, context}) => {
    if (context?.hasLoadingAttachments) {
      return false;
    }
    return attachmentCount > prevAttachmentCount;
  },

  [AiChatProTipType.LOW_RES_ATTACHMENT]: (_config, {attachmentCount, prevAttachmentCount, context}) => {
    if (context?.hasLoadingAttachments) {
      return false;
    }
    const dims = context?.lastAttachmentDimensions;
    if (!dims) {
      return false;
    }
    return attachmentCount > prevAttachmentCount && (dims.width < LOW_RES_THRESHOLD || dims.height < LOW_RES_THRESHOLD);
  },

  [AiChatProTipType.REFER_OLDER_VERSION]: (config, {prompt}) => {
    return hasKeywordMatch(prompt, config);
  },

  [AiChatProTipType.EXACT_TEXT_PROMPTING]: (config, {prompt}) => {
    return hasKeywordMatch(prompt, config) && !TEXTURE_REGEX.test(prompt);
  },

  [AiChatProTipType.SPELLING_ISSUES]: (config, {prompt}) => {
    return hasKeywordMatch(prompt, config);
  },

  [AiChatProTipType.LONG_CHAT_CONTEXT]: (_config, {context}) => {
    return Boolean(context && context.userMessageCount >= LONG_CHAT_MIN_MESSAGES);
  },

  [AiChatProTipType.VAGUE_PROMPT]: (_config, {context}) => {
    if (!context?.firstUserMessageLength || context.userMessageCount !== 1) {
      return false;
    }
    return context.firstUserMessageLength <= VAGUE_PROMPT_MAX_LENGTH;
  },

  [AiChatProTipType.TOO_LONG_TEXT]: (_config, {prompt}) => {
    return prompt.length >= TOO_LONG_PROMPT_MIN_LENGTH;
  },

  [AiChatProTipType.EDITING_TIPS]: (_config, {context}) => {
    return Boolean(context?.hasGeneratedDesign);
  },
};

/**
 * Returns the highest-priority config whose trigger conditions are met,
 * excluding already-seen and already-shown-in-chat tips.
 */
export function findMatchingProTip(triggerContext: TriggerContext, seenTips: Set<string>, shownInChat: Set<string>): ProTipConfig | null {
  if (triggerContext.context && !triggerContext.context.isNewChat) {
    return null;
  }

  const matches: ProTipConfig[] = [];

  for (const config of PRO_TIP_CONFIGS) {
    if (shownInChat.has(config.type)) {
      continue;
    }
    if (!config.persistent && seenTips.has(config.type)) {
      continue;
    }

    const evaluator = TRIGGER_EVALUATORS[config.type];
    if (evaluator(config, triggerContext)) {
      matches.push(config);
    }
  }

  if (matches.length === 0) {
    return null;
  }

  matches.sort((a, b) => a.priority - b.priority);
  return matches[0];
}
